/**
 * AvailabilityBlockDialog — "Zablokuj dostępność" create-window dialog.
 *
 * Presents a date-range picker and a FREE/UNAVAILABLE state selector. Calls
 * onSave with the entered values on confirm — the caller owns invoking the
 * create-availability-window mutation and closing the dialog on success. On
 * API error the caller must NOT close the dialog (per availability-windows
 * spec: "the dialog remains open with the entered values intact") — this
 * component never closes or resets itself, so leaving it mounted is enough.
 *
 * Props-only component. No data fetching, no mutation call.
 */

import * as React from 'react';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { Button } from '@ooh/ui/components/button';
import type { AvailabilityWindowState } from '@ooh/contracts';
import {
  AvailabilityWindowFormFields,
  isAvailabilityWindowFormValid,
} from './availability-window-form-fields';

export interface AvailabilityBlockDialogLabels {
  title: string;
  bodyIntro: string;
  dateRangeLabel: string;
  dateRangePlaceholder: string;
  stateLabel: string;
  freeOptionLabel: string;
  unavailableOptionLabel: string;
  statePlaceholder: string;
  saveLabel: string;
  cancelLabel: string;
}

export interface AvailabilityBlockDialogValues {
  dateFrom: string;
  dateTo: string;
  state: AvailabilityWindowState;
}

export interface AvailabilityBlockDialogProps {
  labels: AvailabilityBlockDialogLabels;
  /** True while the create-window mutation is in flight — disables Save. */
  isPending?: boolean;
  onClose: () => void;
  onSave: (values: AvailabilityBlockDialogValues) => void;
}

/**
 * Builds the full AvailabilityBlockDialogLabels object from a t() function.
 * Shared between /reservations and /media-units/$id to avoid copy-paste drift.
 *
 * @param t - Translation function from useTranslation('reservations')
 */
export function buildAvailabilityBlockDialogLabels(
  t: (key: string) => string,
): AvailabilityBlockDialogLabels {
  return {
    title: t('availabilityWindow.dialogTitle'),
    bodyIntro: t('availabilityWindow.dialogBodyIntro'),
    dateRangeLabel: t('availabilityWindow.dateRangeLabel'),
    dateRangePlaceholder: t('availabilityWindow.dateRangePlaceholder'),
    stateLabel: t('availabilityWindow.stateLabel'),
    freeOptionLabel: t('availabilityWindow.state.FREE'),
    unavailableOptionLabel: t('availabilityWindow.state.UNAVAILABLE'),
    statePlaceholder: t('availabilityWindow.statePlaceholder'),
    saveLabel: t('availabilityWindow.saveLabel'),
    cancelLabel: t('dialogs.cancelBtnLabel'),
  };
}

export function AvailabilityBlockDialog({
  labels,
  isPending = false,
  onClose,
  onSave,
}: AvailabilityBlockDialogProps) {
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [state, setState] = React.useState<AvailabilityWindowState | ''>('');

  const isValid = isAvailabilityWindowFormValid(dateFrom, dateTo, state);

  const handleSave = () => {
    if (!isValid || state === '') return;
    onSave({ dateFrom, dateTo, state });
  };

  return (
    <ModalDialog
      header={<span className="text-[15px] font-semibold">{labels.title}</span>}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isPending}>
            {labels.cancelLabel}
          </Button>
          <Button
            size="sm"
            data-testid="availability-block-save-btn"
            onClick={handleSave}
            disabled={!isValid || isPending}
          >
            {labels.saveLabel}
          </Button>
        </div>
      }
      onClose={onClose}
    >
      <div className="space-y-4">
        <p className="text-[13px] text-muted-foreground">{labels.bodyIntro}</p>
        <AvailabilityWindowFormFields
          labels={labels}
          dateFrom={dateFrom}
          dateTo={dateTo}
          state={state}
          onRangeChange={({ from, to }) => {
            setDateFrom(from);
            setDateTo(to);
          }}
          onStateChange={setState}
          testIds={{
            // eslint-disable-next-line i18next/no-literal-string -- test id, not user-facing
            dateRange: 'availability-block-date-range',
            // eslint-disable-next-line i18next/no-literal-string -- test id, not user-facing
            stateSelect: 'availability-block-state-select',
          }}
          className="text-sm"
        />
      </div>
    </ModalDialog>
  );
}
